import type { Breadcrumb, ErrorEvent } from '@sentry/nextjs'
import { sanitize } from '@/lib/axiom/redaction'
import { shouldDropRealtimeTransientCdcEvent } from './realtime-cdc-sentry-suppression'

/** Pattern matching sensitive query parameter names. */
const SENSITIVE_PARAM = /token|key|secret|password|auth/i
const NATIVE_SUPABASE_AUTO_ERROR_MECHANISM_PREFIX = 'auto.db.supabase.'

function isNativeSupabaseAutoErrorEvent(event: ErrorEvent): boolean {
  return (
    event.exception?.values?.some((value) => {
      const mechanismType = value.mechanism?.type
      return (
        typeof mechanismType === 'string' &&
        mechanismType.startsWith(NATIVE_SUPABASE_AUTO_ERROR_MECHANISM_PREFIX)
      )
    }) ?? false
  )
}

/**
 * Redact values of sensitive query parameters in a URL-style string.
 * Handles both `?key=val&key2=val2` and `key=val&key2=val2` (no leading `?`).
 */
export function redactSensitiveParams(input: string): string {
  return input.replace(
    /(^|[?&])([^=&]+)=([^&]*)/g,
    (_match, prefix: string, paramKey: string, value: string) =>
      SENSITIVE_PARAM.test(paramKey)
        ? `${prefix}${paramKey}=[filtered]`
        : `${prefix}${paramKey}=${value}`,
  )
}

/**
 * Sentry `beforeSend` hook — strips PII from error events before they
 * leave the client/server/edge runtime.
 */
export function scrubEvent(event: ErrorEvent): ErrorEvent | null {
  if (shouldDropRealtimeTransientCdcEvent(event)) return null
  if (isNativeSupabaseAutoErrorEvent(event)) return null

  if (event.request) {
    // Strip request body
    delete event.request.data

    // Strip sensitive headers (case-insensitive)
    const headers = event.request.headers
    if (headers) {
      for (const key of Object.keys(headers)) {
        if (
          ['authorization', 'cookie', 'x-supabase-auth'].includes(
            key.toLowerCase(),
          )
        ) {
          delete headers[key]
        }
      }
    }

    // Scrub sensitive query string params
    const qs = event.request.query_string
    if (typeof qs === 'string') {
      event.request.query_string = redactSensitiveParams(qs)
    } else if (Array.isArray(qs)) {
      // Array of tuples: [key, value][]
      for (const tuple of qs) {
        if (SENSITIVE_PARAM.test(tuple[0])) {
          tuple[1] = '[filtered]'
        }
      }
    } else if (qs !== null && qs !== undefined && typeof qs === 'object') {
      // Record<string, string>
      const record = qs as Record<string, string>
      for (const key of Object.keys(record)) {
        if (SENSITIVE_PARAM.test(key)) {
          record[key] = '[filtered]'
        }
      }
    }
  }

  // Sanitize extras
  if (event.extra) {
    event.extra = sanitize(event.extra as Record<string, unknown>) as Record<
      string,
      unknown
    >
  }

  return event
}

/**
 * Sentry `beforeBreadcrumb` hook — strips form input values and
 * redacts sensitive URL params from fetch/XHR breadcrumbs.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // Strip form field values
  if (breadcrumb.category === 'ui.input') {
    delete breadcrumb.data
  }

  // Redact sensitive query params in fetch/XHR URLs
  if (
    (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
    breadcrumb.data?.url &&
    typeof breadcrumb.data.url === 'string' &&
    SENSITIVE_PARAM.test(breadcrumb.data.url)
  ) {
    breadcrumb.data.url = redactSensitiveParams(breadcrumb.data.url)
  }

  return breadcrumb
}
